import { useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import { useOlayOptions, useUygunsuzlukAc, useUygunsuzluklar, useKapilar } from '../api/queries';
import { UYGUNSUZLUK_DURUM_ETIKET } from '../i18n/tr';
import { DofSayaci } from './olay-dof-sayaci';
import { BosListeNotu, QueryStatus } from './parts';
import { Rozet, Sayfalama, SuzgecGostergesi, KapiSebebi } from '../sozlesme';
import { ANLAM } from '../theme';
import { gunGirdisiIso, tarihGoster } from '../lib/format';
import { useVarsayilanGun } from '../lib/saha-gunu';

/**
 * UYGUNSUZLUK (BULGU) LİSTESİ — Tur 2½a (Kapsam §24 + §24.1).
 *
 * ⚠️ **OLAY DESENİNİN KOPYASIDIR** (U11): yeni sözleşme kararı üretilmedi —
 * P4 (sayfalama + görünür süzgeç + toplam) · DÖF sayacı AYNI bileşen ve AYNI
 * renkler (`DofSayaci`) · durum rozeti · anonim rozeti.
 *
 * ⚠️ **DURUM SÜZGECİ SUNUCUDA** (liste ucu `?durum=`): tanınmayan değer
 * sessizce "tümü"ye düşmez. *Kapalı* ve *İşlemde* burada süzgeç DEĞİLDİR —
 * ikisi de `reported` token'ının TÜRETİLMİŞ hâlleridir (§24.1/4) ve satırda
 * DÖF sayacından okunur; token gibi süzdürmek "kapalı olmayan reported"
 * diye var olmayan bir küme uydururdu.
 */
export function UygunsuzluklarScreen(): React.JSX.Element {
  const [durum, setDurum] = useState<string | null>(null);
  const [sayfa, setSayfa] = useState(1);
  const [bildirimAcik, setBildirimAcik] = useState(false);
  const liste = useUygunsuzluklar({ durum: durum ?? undefined, sayfa });
  const navigate = useNavigate();
  // P13-b (madde 225): bildirim kapısı SUNUCUDAN (iç kullanıcılar) — ekran şapka dizgesi yazmaz.
  const kapilar = useKapilar();
  const salt = !(kapilar.data?.uygunsuzlukBildirimKapisi.yazabilir ?? false);

  return (
    <Stack data-testid="uygunsuzluklar-ekrani">
      <Group justify="space-between">
        <Title order={3}>Uygunsuzluklar</Title>
        {!salt && (
          <Button data-testid="uygunsuzluk-bildir-ac" onClick={() => setBildirimAcik((a) => !a)}>
            {bildirimAcik ? 'Vazgeç' : 'Uygunsuzluk bildir'}
          </Button>
        )}
        {kapilar.data && (
          <KapiSebebi
            durum={kapilar.data.uygunsuzlukBildirimKapisi}
            nesne="Uygunsuzluğu"
            fiil="bildirir"
            testId="uygunsuzluk-bildir-yazamama-sebebi"
          />
        )}
      </Group>
      <Text size="sm" c="dimmed">
        Bir şey mi yaşandı, yoksa bir bozukluk mu gördün? Yaşanan bir şey Olaylar'a, görülen bir
        bozukluk buraya kaydedilir.
      </Text>

      {bildirimAcik && <BildirimFormu onKapat={() => setBildirimAcik(false)} />}

      <Group>
        <Select
          label="Durum"
          data-testid="uygunsuzluk-durum-suzgeci"
          placeholder="Tümü"
          value={durum}
          onChange={(v) => {
            setDurum(v);
            setSayfa(1);
          }}
          clearable
          data={[
            { value: 'reported', label: 'Bildirildi' },
            { value: 'rejected', label: 'Reddedildi' },
            { value: 'moved', label: 'Taşındı' },
          ]}
        />
        <SuzgecGostergesi
          etkinSayisi={durum ? 1 : 0}
          onTemizle={() => {
            setDurum(null);
            setSayfa(1);
          }}
        />
      </Group>

      <QueryStatus queries={[liste]} />
      {liste.isSuccess &&
        (liste.data.satirlar.length === 0 ? (
          <BosListeNotu mesaj="Süzgece uyan uygunsuzluk kaydı yok." />
        ) : (
          <>
            <Table striped highlightOnHover data-testid="uygunsuzluk-tablosu">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Takip no</Table.Th>
                  <Table.Th>Bulgu</Table.Th>
                  <Table.Th>İşyeri</Table.Th>
                  <Table.Th>Lokasyon</Table.Th>
                  <Table.Th>Tarih</Table.Th>
                  <Table.Th>Durum</Table.Th>
                  <Table.Th>DÖF</Table.Th>
                  <Table.Th>Bildiren</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {liste.data.satirlar.map((satir) => (
                  <Table.Tr
                    key={satir.id}
                    data-testid={`uygunsuzluk-satiri-${satir.takipNo}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/uygunsuzluklar/${satir.id}`)}
                  >
                    <Table.Td>
                      <Link to={`/uygunsuzluklar/${satir.id}`}>{satir.takipNo}</Link>
                    </Table.Td>
                    {/* "Ne gördün?" listenin ASIL sütunudur — takip no bir KİMLİKTİR,
                          bulgu ise kaydın kendisi. */}
                    <Table.Td>{satir.aciklama}</Table.Td>
                    <Table.Td>{satir.workplaceAd}</Table.Td>
                    <Table.Td>{satir.locationAd ?? '—'}</Table.Td>
                    <Table.Td>{tarihGoster(satir.tarih)}</Table.Td>
                    <Table.Td>
                      <Rozet anlam={satir.durum === 'rejected' ? 'engel' : 'bilgi'}>
                        {UYGUNSUZLUK_DURUM_ETIKET[satir.durum]}
                      </Rozet>
                    </Table.Td>
                    <Table.Td>
                      <DofSayaci
                        bosCizilmez
                        toplam={satir.dof.toplam}
                        acik={satir.dof.acik}
                        testId={`uygunsuzluk-dof-${satir.takipNo}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      {satir.anonim ? (
                        <Rozet anlam="bilgi" testId={`uygunsuzluk-anonim-${satir.takipNo}`}>
                          Anonim
                        </Rozet>
                      ) : (
                        (satir.bildirenEtiket ?? '—')
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Sayfalama
              sayfa={liste.data.sayfa}
              sayfaBoyutu={liste.data.sayfaBoyutu}
              toplam={liste.data.toplam}
              onDegis={setSayfa}
            />
          </>
        ))}
    </Stack>
  );
}

/**
 * U3 — BİLDİRİM FORMU. Alan sırası SABİTTİR: işyeri → lokasyon → tarih →
 * "Ne gördün?" → "Önerin var mı?".
 * ⚠️ **SINIFLANDIRMA ALANI YOKTUR** — çalışana sınıflandırma yükü binmez.
 */
function BildirimFormu({ onKapat }: { onKapat: () => void }): React.JSX.Element {
  const options = useOlayOptions();
  const ac = useUygunsuzlukAc();
  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [locationNodeId, setLocationNodeId] = useState<string | null>(null);
  // Madde 302: varsayılan "bugün" SAHA GÜNÜ (sunucudan) — kullanıcı değiştirir.
  const [tarih, setTarih] = useVarsayilanGun();
  const [aciklama, setAciklama] = useState('');
  const [oneri, setOneri] = useState('');

  const isyerleri = options.data?.workplaces ?? [];
  // İşyeri SEÇİLİ GELİR (U3/1) — tek işyeri varsa soru bile sorulmaz.
  const secili = workplaceId ?? (isyerleri.length > 0 ? isyerleri[0]!.id : null);
  const lokasyonlar = (options.data?.lokasyonlar ?? []).filter((l) => l.workplaceId === secili);

  return (
    <Stack data-testid="uygunsuzluk-formu">
      {ac.error && (
        <Alert color={ANLAM.engel} role="alert" data-testid="uygunsuzluk-hata">
          {ac.error.message}
        </Alert>
      )}
      <Select
        label="İşyeri"
        data-testid="uygunsuzluk-isyeri"
        value={secili}
        onChange={(v) => {
          setWorkplaceId(v);
          setLocationNodeId(null);
        }}
        data={isyerleri.map((w) => ({ value: w.id, label: w.ad }))}
      />
      <Select
        label="Lokasyon"
        data-testid="uygunsuzluk-lokasyon"
        placeholder="Seçiniz (isteğe bağlı)"
        value={locationNodeId}
        onChange={setLocationNodeId}
        clearable
        data={lokasyonlar.map((l) => ({ value: l.id, label: l.ad }))}
      />
      <TextInput
        type="date"
        label="Tarih"
        data-testid="uygunsuzluk-tarih"
        value={tarih}
        onChange={(e) => setTarih(e.currentTarget.value)}
      />
      <Textarea
        label="Ne gördün?"
        data-testid="uygunsuzluk-aciklama"
        minRows={3}
        value={aciklama}
        onChange={(e) => setAciklama(e.currentTarget.value)}
      />
      <Textarea
        label="Önerin var mı?"
        description="İsteğe bağlı"
        data-testid="uygunsuzluk-oneri"
        minRows={2}
        value={oneri}
        onChange={(e) => setOneri(e.currentTarget.value)}
      />
      <Group>
        <Button
          data-testid="uygunsuzluk-kaydet"
          disabled={!secili || aciklama.trim().length === 0 || ac.isPending}
          onClick={() => {
            // ⚠️ `gunGirdisiIso` burada bir BİÇİMLENDİRİCİ değil, KAPIDIR:
            // geçersiz/olmayan günde null döner ve istek hiç kurulmaz (137
            // dersi). Gövdeye giden değer, kullanıcının girdiği `YYYY-AA-GG`
            // gün metninin kendisidir — dilimleme YAPILMAZ (G11 kod denetimi).
            if (!secili || gunGirdisiIso(tarih) === null) return;
            ac.mutate(
              {
                workplaceId: secili,
                locationNodeId: locationNodeId ?? null,
                tarih,
                aciklama: aciklama.trim(),
                oneri: oneri.trim() ? oneri.trim() : null,
              },
              { onSuccess: onKapat },
            );
          }}
        >
          Bildir
        </Button>
        <Button variant="default" onClick={onKapat}>
          Vazgeç
        </Button>
      </Group>
    </Stack>
  );
}
